class CustomVariantSelector {
  constructor(element) {
    this.element = element;
    this.button = element.querySelector('[data-select-button]');
    this.dropdown = element.querySelector('[data-dropdown]');
    this.options = element.querySelectorAll('[data-option-item]');
    this.selectedText = element.querySelector('[data-selected-text]');
    this.selectedImage = element.querySelector('[data-selected-image]');
    this.colorPreview = element.querySelector('[data-color-preview]');
    this.inputs = element.querySelectorAll('.custom-select-input');
    this.isOpen = false;
    this.isUpdating = false;
    
    // Bind methods para mantener contexto
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    
    this.init();
  }
  
  init() {
    // Remover listeners previos si existen
    this.removeEventListeners();
    
    // Agregar event listener al botón
    this.button.addEventListener('click', this.handleButtonClick);
    
    // Agregar event listeners a las opciones
    this.options.forEach((option, index) => {
      const handleOptionClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (option.classList.contains('disabled') || this.isUpdating) return;
        this.selectOption(option, index);
      };
      option.addEventListener('click', handleOptionClick);
      option._clickHandler = handleOptionClick;
    });
    
    // Event listeners para círculos de color
    if (this.colorPreview) {
      const colorCircles = this.colorPreview.querySelectorAll('[data-color-item]');
      colorCircles.forEach((circle) => {
        const handleCircleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.isUpdating) return;
          const index = parseInt(circle.dataset.colorItem);
          const option = this.options[index];
          if (option && !option.classList.contains('disabled')) {
            this.selectOption(option, index);
          }
        };
        circle.addEventListener('click', handleCircleClick);
        circle._clickHandler = handleCircleClick;
      });
    }
    
    // Event listener para cerrar al hacer clic fuera
    document.addEventListener('click', this.handleDocumentClick);
  }
  
  handleButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggleDropdown();
  }
  
  handleDocumentClick(e) {
    if (!this.element.contains(e.target) && this.isOpen) {
      this.closeDropdown();
    }
  }
  
  removeEventListeners() {
    // Remover listener del botón
    this.button?.removeEventListener('click', this.handleButtonClick);
    
    // Remover listener del documento
    document.removeEventListener('click', this.handleDocumentClick);
    
    // Remover listeners de opciones
    this.options?.forEach(option => {
      if (option._clickHandler) {
        option.removeEventListener('click', option._clickHandler);
        delete option._clickHandler;
      }
    });
    
    // Remover listeners de círculos de color
    if (this.colorPreview) {
      const colorCircles = this.colorPreview.querySelectorAll('[data-color-item]');
      colorCircles?.forEach(circle => {
        if (circle._clickHandler) {
          circle.removeEventListener('click', circle._clickHandler);
          delete circle._clickHandler;
        }
      });
    }
  }
  
  destroy() {
    this.removeEventListeners();
    this.closeDropdown();
  }
  
  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.closeAllOtherSelectors();
      this.openDropdown();
    }
  }
  
  closeAllOtherSelectors() {
    document.querySelectorAll('.custom-select-variant.open').forEach(selector => {
      if (selector !== this.element) {
        selector.classList.remove('open');
        const dropdown = selector.querySelector('[data-dropdown]');
        if (dropdown) dropdown.style.display = 'none';
      }
    });
  }
  
  openDropdown() {
    this.element.classList.add('open');
    this.dropdown.style.display = 'block';
    this.isOpen = true;
  }
  
  closeDropdown() {
    this.element.classList.remove('open');
    this.dropdown.style.display = 'none';
    this.isOpen = false;
  }
  
  selectOption(option, index) {
    if (this.isUpdating) {
      console.log('Selector ocupado, operación cancelada');
      return;
    }
    
    this.isUpdating = true;
    console.log('Seleccionando opción:', index);
    
    // Actualizar UI inmediatamente
    this.updateUI(option, index);
    
    const input = option.querySelector('.custom-select-input');
    if (input && !input.checked) {
      // Actualizar estados de selección
      this.options.forEach(opt => opt.classList.remove('selected'));
      option.classList.add('selected');
      this.inputs.forEach(inp => inp.checked = false);
      input.checked = true;
      
      // Pausar el sistema global
      window.customVariantSelectors.pauseSystem();
      
      // Disparar evento de cambio
      setTimeout(() => {
        try {
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          input.dispatchEvent(changeEvent);
          console.log('Evento change disparado');
        } catch (error) {
          console.error('Error al disparar evento:', error);
        }
        
        // Reanudar el sistema después de que Shopify procese el cambio
        setTimeout(() => {
          console.log('Reanudando sistema');
          this.isUpdating = false;
          window.customVariantSelectors.resumeSystem();
        }, 1000); // Aumentado a 1000ms para dar más tiempo
      }, 150);
    } else {
      this.isUpdating = false;
    }
    
    // Cerrar dropdown
    this.closeDropdown();
  }
  
  updateUI(option, index) {
    const value = option.dataset.value;
    
    if (this.selectedText) {
      this.selectedText.textContent = value;
    }
    
    if (this.selectedImage && option.dataset.imageUrl) {
      const img = this.selectedImage.querySelector('img');
      if (img) {
        img.src = option.dataset.imageUrl;
        img.alt = value;
      }
    }
    
    if (this.colorPreview) {
      const circles = this.colorPreview.querySelectorAll('[data-color-item]');
      circles.forEach((circle, i) => {
        circle.classList.toggle('active', i === index);
      });
    }
  }
}

// Sistema global de gestión
window.customVariantSelectors = {
  instances: [],
  observer: null,
  isInitializing: false,
  isPaused: false,
  initTimeout: null,
  
  init(force = false) {
    if (this.isInitializing && !force) {
      console.log('Ya se está inicializando, cancelando...');
      return;
    }
    
    // Cancelar cualquier inicialización pendiente
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
    }
    
    console.log('Inicializando selectores...');
    this.isInitializing = true;
    
    // Destruir instancias previas
    this.destroy();
    
    // Encontrar todos los selectores únicos
    const selectors = document.querySelectorAll('.custom-select-variant');
    const uniqueSelectors = Array.from(selectors).filter((el, index, self) => 
      el.id ? index === self.findIndex((t) => t.id === el.id) : true
    );
    
    console.log(`Encontrados ${uniqueSelectors.length} selectores`);
    
    // Crear instancias
    uniqueSelectors.forEach((el) => {
      try {
        this.instances.push(new CustomVariantSelector(el));
      } catch (error) {
        console.error('Error al crear instancia:', error);
      }
    });
    
    this.isInitializing = false;
    console.log('Inicialización completa');
  },
  
  destroy() {
    console.log('Destruyendo instancias...');
    this.instances.forEach(instance => {
      try {
        instance.destroy();
      } catch (error) {
        console.error('Error al destruir instancia:', error);
      }
    });
    this.instances = [];
  },
  
  pauseSystem() {
    console.log('Sistema pausado');
    this.isPaused = true;
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  
  resumeSystem() {
    console.log('Reanudando sistema...');
    this.isPaused = false;
    
    // Esperar un poco antes de reinicializar para evitar conflictos
    this.initTimeout = setTimeout(() => {
      this.init(true);
      if (this.observer) {
        this.setupObserver();
      }
    }, 300);
  },
  
  setupObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    console.log('Configurando observador...');
    
    this.observer = new MutationObserver((mutations) => {
      if (this.isPaused || this.isInitializing) {
        return;
      }
      
      const hasRelevantChanges = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) {
            return node.matches?.('variant-picker') || 
                   node.querySelector?.('variant-picker') ||
                   node.classList?.contains('custom-select-variant');
          }
          return false;
        });
      });
      
      if (hasRelevantChanges) {
        console.log('Cambios detectados en el DOM');
        // Reinicializar después de un pequeño delay
        if (this.initTimeout) {
          clearTimeout(this.initTimeout);
        }
        this.initTimeout = setTimeout(() => {
          this.init(true);
        }, 500);
      }
    });
    
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
};

// Función de inicialización
function initCustomSelectors() {
  console.log('Iniciando custom selectors...');
  if (document.querySelector('.custom-select-variant')) {
    window.customVariantSelectors.init();
    window.customVariantSelectors.setupObserver();
  } else {
    console.log('No se encontraron selectores personalizados');
  }
}

// Inicialización en carga del documento
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomSelectors);
} else {
  initCustomSelectors();
}

// Manejo de eventos de Shopify
document.addEventListener('shopify:section:load', () => {
  console.log('Sección de Shopify cargada');
  setTimeout(() => {
    window.customVariantSelectors.init(true);
  }, 300);
});

// Cleanup en caso de que la página se descargue
window.addEventListener('beforeunload', () => {
  window.customVariantSelectors.destroy();
});